<script setup>
  import { ref, onMounted, onUnmounted } from "vue";
  import ProductCard from "./ProductCard.vue";
  import ProductPagination from "./ProductPagination.vue";
  import ProductSort from "./ProductSort.vue";
  import ProductFilter from "./ProductFilter.vue";

  const props = defineProps({
    categoryId: Number,
    breadcrumb: Array,
    paginationStyle: { type: String, default: "standard" },
    perPageOptions: {
      type: Object,
      default: () => ({ options: [12, 24, 48], default: 12 })
    },
    userEmail: {
      type: String,
      default: null,
    },
    categories: Array
  });

  const products = ref([]);
  const paginator = ref(null);
  const perRowTemplate = ref("1fr 1fr 1fr 1fr 1fr 1fr");
  const isListView = ref(false);
  const isFilterOpen = ref(false);

  const addProductToCart = (productId, quantity = 1) => {
    console.log("Adicionando produto ID:", productId, "Quantidade:", quantity);

    if (window.cart && typeof window.cart.update === "function") {
      window.cart.update({
        "items": [{
          "id": productId,
          "quantity": quantity,
          "add": true
        }],
        "action": "saveInProgress"
      }, (response) => {
        const loginPainel = document.getElementById("loginPainel");
        if (loginPainel) loginPainel.classList.add("open");

        if (window.lastedCart && typeof window.lastedCart.add === "function") {
          window.lastedCart.add("open");
        }

        window.dispatchEvent(new CustomEvent("cart-updated"));
        if (window.Livewire) {
          window.Livewire.dispatch("cart-updated");
        }

      }, (response) => {
        console.error("Erro ao adicionar:", response);
        alert("Erro ao adicionar produto ao carrinho.");
      });
    } else {
      console.error("API do carrinho (window.cart) não está disponível.");
    }
  }


  const toggleFilter = () => {
    isFilterOpen.value = !isFilterOpen.value;
    if (window.innerWidth <= 875.5) {
      document.body.style.overflow = isFilterOpen.value ? "hidden" : "";
    }
    checkScreenSize();
  };

  const closeFilter = () => {
    isFilterOpen.value = false;
    document.body.style.overflow = "";
  };

  const handleFilterChange = (filterLink) => {
    closeFilter();
    if (filterLink) {
      window.location.href = filterLink;
    } else {
      const url = new URL(window.location.href);
      url.searchParams.delete("filter_id");
      window.location.href = url.pathname;
    }
  };

  const sortFilters = ref({
    name: [
      { name: "A-Z", value: "asc", selected: false },
      { name: "Z-A", value: "desc", selected: false }
    ]
  });

  const fetchProducts = async (page = 1) => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const lang = window.lang || "pt";
      const apiUrl = new URL(`${window.location.origin}/api/${lang}/get-products/`);

      const requestData = {
        categoryId: props.categoryId ?? 0,
        perPage: urlParams.get("perPage") || props.perPageOptions.default,
        filter_id: urlParams.get("filter_id") || 0,
        page: page
      };

      if (urlParams.has("name")) {
        requestData.name = urlParams.get("name");
      }
      if (urlParams.has("mostRecent")) {
        requestData.mostRecent = urlParams.get("mostRecent") === "true";
      }


      apiUrl.searchParams.set("categoryId", requestData.categoryId);
      apiUrl.searchParams.set("perPage", requestData.perPage);
      apiUrl.searchParams.set("filter_id", requestData.filter_id);
      apiUrl.searchParams.set("page", page);
      if (requestData.name) apiUrl.searchParams.set("name", requestData.name);
      if (requestData.mostRecent) apiUrl.searchParams.set("mostRecent", "true");

      const response = await fetch(apiUrl, {
        method: "POST",
        body: JSON.stringify(requestData),
        headers: { "Content-Type": "application/json" }
      });

      const data = await response.json();
      products.value = data.products;
      paginator.value = data.paginator;

      checkScreenSize();
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);
    }
  };

  const mountGridTemplate = (perRow) => {
    let template = "";
    for (let i = 0; i < perRow; i++) template += "1fr ";
    perRowTemplate.value = template.trim();
    isListView.value = String(perRow) === "1";
  };

  const checkScreenSize = () => {
    if (window.innerWidth <= 675.5) {
      perRowTemplate.value = "1fr";
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      const perRow = urlParams.get("perRow") || 6;
      mountGridTemplate(perRow);
    }

    if (window.innerWidth > 875.5) {
      document.body.style.overflow = "";
    }
  };

  onMounted(() => {
    fetchProducts();
    window.addEventListener("resize", checkScreenSize);
  });

  onUnmounted(() => {
    window.removeEventListener("resize", checkScreenSize);
    document.body.style.overflow = "";
  });
</script>

<template>
  <div class="container-product-list">
    <div
      class="filter-backdrop"
      :class="{ 'active': isFilterOpen }"
      @click="closeFilter"></div>
    <div class="product-list">
      <div class="content-sort">
        <ProductSort
          :breadcrumb="breadcrumb"
          :perPageOptions="perPageOptions"
          @change-per-row="mountGridTemplate"
          @change-per-page="fetchProducts"
          @change-order="fetchProducts(1)"
          :sort-filters="sortFilters" />
      </div>

      <div class="filter-trigger-bar">
        <button class="btn-toggle-filters"
                :class="{ 'active': isFilterOpen }"
                @click="toggleFilter">
          <span class="burger-icon">
            <svg width="16" height="12" viewBox="0 0 30 20">
              <rect width="30" height="3" fill="currentColor" />
              <rect y="9" width="30" height="3" fill="currentColor" />
              <rect y="18" width="30" height="3" fill="currentColor" />
            </svg>
          </span>
          <span>{{ isFilterOpen ? "Ocultar Filtros" : "Mostrar Filtros" }}</span>
        </button>
      </div>
      <div class="filter-mobile">
        <button @click="toggleFilter">
          Filtros
        </button>
      </div>
      <div class="container-filter-product"
           :class="{ 'filter-is-open': isFilterOpen }">
        <aside class="content-filter" :class="{ 'sidebar-open': isFilterOpen }">
          <div class="filter-sidebar-content">
            <ProductFilter
              :categories="categories"
              @filter-updated="handleFilterChange" />
          </div>
        </aside>

        <main class="content-cards">
          <div class="container-cards"
               :style="{ 'grid-template-columns': perRowTemplate }"
               :class="{ 'product-list-view': isListView }">

            <ProductCard
              v-for="product in products"
              :key="product.id"
              :product="product"
              :isListView="isListView"
              :user-email="userEmail"
              @add-to-cart="addProductToCart"
            />
          </div>
        </main>
      </div>
      <div class="content-pagination">
        <ProductPagination
          :pagination="paginator"
          :paginationStyle="paginationStyle"
          @change-page="fetchProducts" />
      </div>
    </div>
  </div>
</template>

